import {
  EMPTY_CUP,
  MAX_DOSE,
  MAX_SHOTS,
  coffeeDifferences,
  coffeeMismatch,
  doseStatus,
  nameFor,
  type CoffeeBuild,
  type CoffeeCup,
  type Heated,
  type Syrup,
} from "./coffee";
import { customerGone, leadSeconds, type Customer } from "./customers";
import { DISHES, DISH_ORDER, type DishKind } from "./dishes";
import {
  DRINK_NAMES,
  EMPTY,
  FRIES_BURNT,
  MAX_FILL,
  drinkMismatch,
  friesDoneness,
  glassStatus,
  mismatch,
  needsBasket,
  needsFlip,
  pattyDoneness,
  type Build,
  type Contents,
  type Drink,
  type Fries,
  type Glass,
  type Patty,
  type Topping,
} from "./food";
import type { CafeSetup, KitchenSetup, ShiftSetup, StarGoal } from "./restaurants";
import { nextRandom } from "./rng";

// One shift at a restaurant, as plain data and pure functions: `tick` moves
// the clock, `act` applies what the player did, and both return a new state
// without touching the old one, so the engine can keep it in a ref and tests
// can play a whole shift in a loop. Where things are in the kitchen, and
// walking between them, is `kitchen.ts`'s business; this is only what's in
// each place and in your hands.
//
// Customers place tickets. At a `window` restaurant (the Burger Shack) you
// cook each order: patties on the flat-top (flipped once a side's done),
// fries in the fryer, built in a clean basket on the assembly board with the
// ticket's toppings, then handed out of the pickup window. At a `shelf`
// restaurant (the dishwashing-only diner and noodle bar) the kitchen plates
// orders itself whenever the clean shelf has the dishes.
//
// Either way the dishes come back dirty into the bus tub when the customer's
// done. You carry a dish to the sink and scrub it, and it drops into the
// rack; a run of the machine washes the rack, and you carry the clean rack
// to the shelf. Fall behind and customers walk out, and the tub overflows
// (dishes break).

export interface Dish {
  id: number;
  kind: DishKind;
  /** Layers of grime left. */
  grime: number;
  /** Layers it came in with. */
  maxGrime: number;
  /** How far through the current layer the sponge has got, 0..1. */
  scrubbed?: number;
}

export interface Ticket {
  id: number;
  /** The order's number for the day, from 1: what the HUD and the window call it. */
  no: number;
  item: string;
  dishes: DishKind[];
  /** At a window restaurant: what goes in the basket. */
  build?: Build;
  /** At a cafe: what goes in the cup, and whose it is (called out at the window). */
  coffee?: CoffeeBuild;
  name?: string;
  placedAt: number;
}

export interface Diner {
  dishes: DishKind[];
  doneAt: number;
}

export interface MachineLoad {
  dishes: Dish[];
  startedAt: number;
  doneAt: number;
}

/** A clean basket with an order going into it. */
export interface Basket {
  dish: Dish;
  contents: Contents;
}

/** A clean soda cup, and what's been poured into it. */
export interface Cup extends Glass {
  dish: Dish;
}

export type ShiftEventKind = "served" | "break" | "walkout" | "burnt" | "closing";

export interface ShiftEvent {
  id: number;
  at: number;
  kind: ShiftEventKind;
  text: string;
  /** The tip, on a served order. */
  tip?: number;
}

export interface ShiftStats {
  orders: number;
  served: number;
  walkouts: number;
  breaks: number;
  tips: number;
}

/** What's in your hands: a dirty dish, a clean rack, or food on its way to the window. */
export type Held =
  | { kind: "dish"; dish: Dish }
  | { kind: "rack"; dishes: Dish[] }
  | { kind: "patty"; patty: Patty }
  | { kind: "fries"; fries: Fries }
  | { kind: "basket"; basket: Basket }
  | { kind: "cup"; cup: Cup }
  // The coffee truck's: a portafilter being dosed at the grinder, a shot in its
  // glass, a pitcher of milk, and a paper cup of coffee.
  | { kind: "portafilter"; dose: number }
  | { kind: "shot"; shot: Heated }
  | { kind: "pitcher"; milk: Heated }
  | { kind: "coffee"; cup: CoffeeCup };

export interface ShiftState {
  setup: ShiftSetup;
  /** Seconds since clock-in. */
  t: number;
  rng: number;
  nextId: number;
  nextOrderAt: number;
  tickets: Ticket[];
  diners: Diner[];
  shelf: Partial<Record<DishKind, number>>;
  tub: Dish[];
  held: Held | null;
  sink: Dish | null;
  rack: Dish[];
  machine: MachineLoad | null;
  /** The flat-top's spots, left to right. Empty at a shelf restaurant. */
  griddle: (Patty | null)[];
  /** The fryer's baskets. */
  fryer: (Fries | null)[];
  /** The baskets on the assembly board's spots (`BOARD_SPOTS` at a window restaurant). */
  board: (Basket | null)[];
  /** The board spot you last worked at: toppings go on its basket (`boardBasket`). */
  boardFocus: number;
  /** At a window restaurant: the people out front, from walking up to walking off (`customers.ts`). */
  customers: Customer[];
  /** At a cafe: a shot pulling under each group head of the espresso machine. */
  groups: (Heated | null)[];
  /** A pitcher of milk on the steam wand. */
  wand: Heated | null;
  /** The paper cup on the bar, being made. */
  bar: CoffeeCup | null;
  /** Drinks poured and waiting on the window's ledge for the rest of their order. */
  ledge: Cup[];
  stats: ShiftStats;
  /** The latest few things that happened, newest last, for the UI's toasts. */
  events: ShiftEvent[];
  /**
   * The day's done: it's past closing time (`setup.shiftSeconds`), the last
   * orders are out (or walked) and every dish is washed and back on the
   * shelf. Closing time itself only stops new orders; the clock runs on
   * while you finish up.
   */
  over: boolean;
}

export type ShiftAction =
  /** Pick a dish up out of the tub (the oldest, without an id). Needs empty hands. */
  | { type: "take"; dishId?: number }
  /** Put the dirty dish you're holding back in the tub. */
  | { type: "putBack" }
  /** Drop the dirty dish you're holding into the (empty) sink. */
  | { type: "toSink" }
  /**
   * Scrub the dish in the sink for `seconds` (the engine sends each frame's
   * time while Use is held). Without `seconds`, finish the current layer.
   * Clean, it drops into the rack if there's room. Needs free hands.
   */
  | { type: "scrub"; seconds?: number }
  /** Run the machine on the rack, or take the clean rack out when it's done (needs empty hands). */
  | { type: "machine" }
  /** Lift a clean hand-washed dish (a wok) out of the sink. */
  | { type: "lift" }
  /** Unload the clean rack, or the clean hand-washed dish, you're carrying onto the shelf; or put an empty basket back. */
  | { type: "shelve" }
  /** Take a clean basket off the shelf to build an order in. Holding cooked food, it goes in the basket. */
  | { type: "takeBasket" }
  /** A raw patty from the fridge. */
  | { type: "takePatty" }
  /** A portion of frozen fries from the freezer. */
  | { type: "takeFries" }
  /**
   * At a spot on the flat-top: lay the patty you're holding down, flip the
   * one there once its bottom's browned, or take it off once it's done
   * (straight into the basket you're holding, if you are).
   */
  | { type: "griddle"; slot: number }
  /** At a fryer basket: drop the fries you're holding in, or lift them out (done ones straight into the basket you're holding). */
  | { type: "fryer"; slot: number }
  /**
   * At a spot on the assembly board: set a basket down, add a patty or fries
   * to it, or pick it up (holding another basket, the two swap). Cooked food
   * with no basket there gets a clean one off the shelf.
   */
  | { type: "board"; slot: number }
  /** Put a topping on the basket you're holding, or else the one you last worked on at the board; or take it back off. */
  | { type: "topping"; topping: Topping }
  /**
   * At the window: hand out the basket you're holding to the ticket it
   * matches (with its drink off the ledge), or a cup: out to a drinks-only
   * ticket, or onto the ledge to wait for its food.
   */
  | { type: "deliver" }
  /** Take a drink back off the window's ledge. */
  | { type: "takeDrink" }
  /** A clean cup from the stack by the soda machine. */
  | { type: "takeCup" }
  /** Hold the cup you're carrying under a nozzle for `seconds` (the engine sends each frame's time while Use is held). */
  | { type: "pour"; drink: Drink; seconds?: number }
  /** Throw out the food you're holding (a basket keeps its basket), or tip out a drink (the cup's dirty then). */
  | { type: "trash" }
  // The coffee truck.
  /** Hold at the grinder: grinds into the portafilter (taking it up if your hands are free) for `seconds`. */
  | { type: "grind"; seconds?: number }
  /** At a group head: lock in a dosed portafilter (the shot starts pulling into a clean shot glass), or take the shot. */
  | { type: "group"; slot: number }
  /** A clean pitcher of cold milk from the fridge. */
  | { type: "takeMilk" }
  /** At the steam wand: set the pitcher on it, or take it off. */
  | { type: "wand" }
  /** A paper cup from the stack. */
  | { type: "takeCoffeeCup" }
  /**
   * At the bar: set a cup down, pour a shot or the milk into the cup there
   * (a fresh cup comes off the stack if there isn't one), or pick it up.
   */
  | { type: "bar" }
  /** A pump of syrup into the cup on the bar, or (again) take it back out. */
  | { type: "pump"; syrup: Syrup };

/** The longest step `tick` takes at once; a longer `dt` is cut to this. */
export const MAX_STEP = 0.25;
const EVENTS_KEPT = 12;
/** Baskets that fit on the assembly board at once, side by side. */
export const BOARD_SPOTS = 2;
/** At most this many patties in one basket. */
export const MAX_PATTIES = 2;
/** Drinks that fit on the window's ledge. */
export const LEDGE_SIZE = 3;
/** A tap of Use at a nozzle pours this long; holding pours for as long as it's held. */
export const POUR_TAP = 0.15;
/** The extra tip for a soda (water's free). */
export const SODA_TIP = 1;

/** The kinds of dish a setup uses, in display order. */
export function setupKinds(setup: ShiftSetup): DishKind[] {
  return DISH_ORDER.filter((k) => setup.stock[k] !== undefined);
}

export function createShift(setup: ShiftSetup, seed: number): ShiftState {
  const k = setup.kitchen;
  const s: ShiftState = {
    setup,
    t: 0,
    rng: seed | 0,
    nextId: 1,
    nextOrderAt: 0,
    tickets: [],
    diners: [],
    shelf: { ...setup.stock },
    tub: [],
    held: null,
    sink: null,
    rack: [],
    machine: null,
    griddle: Array.from({ length: k?.griddleSlots ?? 0 }, () => null),
    fryer: Array.from({ length: k?.fryerSlots ?? 0 }, () => null),
    board: Array.from({ length: setup.service === "window" ? BOARD_SPOTS : 0 }, () => null),
    boardFocus: 0,
    customers: [],
    groups: Array.from({ length: setup.cafe?.groups ?? 0 }, () => null),
    wand: null,
    bar: null,
    ledge: [],
    stats: { orders: 0, served: 0, walkouts: 0, breaks: 0, tips: 0 },
    events: [],
    over: false,
  };
  for (const kind of setup.startTub) s.tub.push(dirtyDish(s, kind));
  s.nextOrderAt = 2 + orderGap(s, 0);
  return s;
}

/** Advance the shift by `dt` seconds (at most `MAX_STEP`). */
export function tick(state: ShiftState, dt: number): ShiftState {
  if (state.over || dt <= 0) return state;
  const s = draft(state);
  const { setup } = s;
  const step = Math.min(dt, MAX_STEP);
  const wasOpen = s.t < setup.shiftSeconds;
  s.t += step;

  if (setup.service === "window") arrive(s);

  while (wasOpen && s.nextOrderAt <= s.t && s.nextOrderAt <= setup.lastOrderAt) {
    placeOrder(s, s.nextOrderAt);
    s.nextOrderAt += orderGap(s, s.nextOrderAt);
  }

  if (setup.kitchen) cook(s, setup.kitchen, step);
  if (setup.cafe) brew(s, setup.cafe, step);

  const stillEating: Diner[] = [];
  for (const diner of s.diners) {
    if (diner.doneAt > s.t) stillEating.push(diner);
    else for (const kind of diner.dishes) bus(s, kind);
  }
  s.diners = stillEating;

  if (setup.service === "shelf") serveFromShelf(s);

  const waiting: Ticket[] = [];
  for (const ticket of s.tickets) {
    if (s.t - ticket.placedAt >= setup.patience) {
      s.stats.walkouts++;
      finish(s, ticket.no, "walkout");
      log(s, "walkout", setup.service === "shelf" ? `${ticket.item} walked out: no clean ${missingFor(s, ticket)}.` : `${ticket.item} walked out: waited too long.`);
    } else waiting.push(ticket);
  }
  s.tickets = waiting;

  if (wasOpen && s.t >= setup.shiftSeconds) {
    // Closing time: no more orders, but whoever's already ordered still waits
    // for theirs (until their patience runs out, as ever). Then the washing-up.
    const open = s.tickets.length;
    log(s, "closing", open ? `Closing time! Finish the last ${open === 1 ? "order" : `${open} orders`}, then wash every dish and put it away.` : "Closing time! Wash every dish and put it away to close up.");
  }
  if (s.t >= setup.shiftSeconds && !s.tickets.length && dishesOut(s) === 0) s.over = true;
  return s;
}

export function act(state: ShiftState, action: ShiftAction): ShiftState {
  if (state.over) return state;
  const held = state.held;
  switch (action.type) {
    case "take": {
      if (held) return state;
      const i = action.dishId === undefined ? 0 : state.tub.findIndex((d) => d.id === action.dishId);
      if (i < 0 || i >= state.tub.length) return state;
      const s = draft(state);
      s.held = { kind: "dish", dish: s.tub.splice(i, 1)[0]! };
      return s;
    }
    case "putBack": {
      // Always: a dish in your hands can go back in the tub even past full (only
      // dishes bused into a full tub break), so your hands are never stuck.
      if (held?.kind !== "dish" || held.dish.grime === 0) return state;
      const s = draft(state);
      s.tub.push(held.dish);
      s.held = null;
      return s;
    }
    case "toSink": {
      if (held?.kind !== "dish" || held.dish.grime === 0 || state.sink) return state;
      const s = draft(state);
      s.sink = { ...held.dish };
      s.held = null;
      settleSink(s);
      return s;
    }
    case "scrub": {
      if (held || !state.sink || state.sink.grime === 0) return state;
      const s = draft(state);
      const dish = { ...s.sink! };
      const layer = DISHES[dish.kind].scrubSeconds;
      let worked = (dish.scrubbed ?? 0) + (action.seconds === undefined ? 1 - (dish.scrubbed ?? 0) : action.seconds / layer);
      while (worked >= 1 - 1e-9 && dish.grime > 0) {
        dish.grime--;
        worked -= 1;
      }
      dish.scrubbed = dish.grime > 0 ? Math.max(0, worked) : 0;
      s.sink = dish;
      settleSink(s);
      return s;
    }
    case "machine": {
      if (state.machine) {
        if (state.t < state.machine.doneAt || held) return state;
        const s = draft(state);
        s.held = { kind: "rack", dishes: s.machine!.dishes };
        s.machine = null;
        return s;
      }
      if (!state.rack.length || held?.kind === "rack") return state;
      const s = draft(state);
      s.machine = { dishes: s.rack, startedAt: s.t, doneAt: s.t + s.setup.machineSeconds };
      s.rack = [];
      settleSink(s);
      return s;
    }
    case "lift": {
      if (held || sinkStatus(state) !== "rinsed") return state;
      const s = draft(state);
      s.held = { kind: "dish", dish: s.sink! };
      s.sink = null;
      return s;
    }
    case "shelve": {
      if (held?.kind === "cup") {
        if (held.cup.drink) return state;
        const s = draft(state);
        s.shelf.tumbler = (s.shelf.tumbler ?? 0) + 1;
        s.held = null;
        return s;
      }
      if (held?.kind === "basket") {
        if (!isEmpty(held.basket.contents)) return state;
        const s = draft(state);
        s.shelf.basket = (s.shelf.basket ?? 0) + 1;
        s.held = null;
        return s;
      }
      if (!held || (held.kind !== "rack" && held.kind !== "dish") || (held.kind === "dish" && held.dish.grime > 0)) return state;
      const s = draft(state);
      for (const dish of held.kind === "rack" ? held.dishes : [held.dish]) s.shelf[dish.kind] = (s.shelf[dish.kind] ?? 0) + 1;
      s.held = null;
      if (s.setup.service === "shelf") serveFromShelf(s);
      return s;
    }
    case "takeBasket": {
      if ((held && !cookedFood(held)) || state.setup.service !== "window" || !state.shelf.basket) return state;
      const s = draft(state);
      s.held = { kind: "basket", basket: { ...cleanBasket(s), contents: held ? addFood(EMPTY, held)! : EMPTY } };
      return s;
    }
    case "takePatty": {
      if (held || !state.setup.kitchen) return state;
      return { ...state, held: { kind: "patty", patty: { sides: [0, 0] } } };
    }
    case "takeFries": {
      if (held || !state.setup.kitchen) return state;
      return { ...state, held: { kind: "fries", fries: { cook: 0 } } };
    }
    case "griddle": {
      const spot = state.griddle[action.slot];
      if (spot === undefined) return state;
      if (held?.kind === "patty" && !spot) {
        const s = draft(state);
        s.griddle[action.slot] = held.patty;
        s.held = null;
        return s;
      }
      if (held?.kind === "basket" && spot && pattyDoneness(spot) === "done") {
        const contents = addFood(held.basket.contents, { kind: "patty", patty: spot });
        if (!contents) return state;
        const s = draft(state);
        s.held = { kind: "basket", basket: { ...held.basket, contents } };
        s.griddle[action.slot] = null;
        return s;
      }
      if (held || !spot) return state;
      const doneness = pattyDoneness(spot);
      // Flipping only helps once the bottom's browned and the top hasn't.
      if (doneness !== "done" && doneness !== "burnt" && !needsFlip(spot)) return state;
      const s = draft(state);
      if (doneness === "done" || doneness === "burnt") {
        s.held = { kind: "patty", patty: spot };
        s.griddle[action.slot] = null;
      } else s.griddle[action.slot] = { sides: [spot.sides[1], spot.sides[0]] };
      return s;
    }
    case "fryer": {
      const spot = state.fryer[action.slot];
      if (spot === undefined) return state;
      if (held?.kind === "fries" && !spot && held.fries.cook === 0) {
        const s = draft(state);
        s.fryer[action.slot] = held.fries;
        s.held = null;
        return s;
      }
      if (held?.kind === "basket" && spot && friesDoneness(spot) === "done") {
        const contents = addFood(held.basket.contents, { kind: "fries", fries: spot });
        if (!contents) return state;
        const s = draft(state);
        s.held = { kind: "basket", basket: { ...held.basket, contents } };
        s.fryer[action.slot] = null;
        return s;
      }
      if (held || !spot) return state;
      const s = draft(state);
      s.held = { kind: "fries", fries: spot };
      s.fryer[action.slot] = null;
      return s;
    }
    case "board": {
      const slot = action.slot;
      const board = state.board[slot];
      if (board === undefined) return state;
      if (!board) {
        if (held?.kind === "basket") {
          const s = draft(state);
          s.board[slot] = held.basket;
          s.boardFocus = slot;
          s.held = null;
          return s;
        }
        // Cooked food and no basket down: a clean one off the shelf, with the food in it.
        if (!held || !cookedFood(held) || !state.shelf.basket) return state;
        const s = draft(state);
        s.board[slot] = { ...cleanBasket(s), contents: addFood(EMPTY, held)! };
        s.boardFocus = slot;
        s.held = null;
        return s;
      }
      const s = draft(state);
      if (!held) {
        // Picked up: toppings now go on the other basket, if there is one.
        s.held = { kind: "basket", basket: board };
        s.board[slot] = null;
        const other = s.board.findIndex(Boolean);
        if (other >= 0) s.boardFocus = other;
        return s;
      }
      s.boardFocus = slot;
      // Two baskets: yours goes down, and you pick up the one that was there.
      if (held.kind === "basket") {
        s.held = { kind: "basket", basket: board };
        s.board[slot] = held.basket;
        return s;
      }
      const contents = addFood(board.contents, held);
      if (!contents) return state;
      s.held = null;
      s.board[slot] = { ...board, contents };
      return s;
    }
    case "topping": {
      const toggle = (b: Basket): Basket => {
        const has = b.contents.toppings.includes(action.topping);
        const toppings = has ? b.contents.toppings.filter((t) => t !== action.topping) : [...b.contents.toppings, action.topping];
        return { ...b, contents: { ...b.contents, toppings } };
      };
      if (held?.kind === "basket") return { ...state, held: { kind: "basket", basket: toggle(held.basket) } };
      const slot = boardSlot(state);
      if (slot < 0) return state;
      const s = draft(state);
      s.board[slot] = toggle(state.board[slot]!);
      return s;
    }
    case "deliver": {
      if (held?.kind === "coffee") {
        const ticket = matchingCoffee(state, held.cup);
        if (!ticket) return state;
        const s = draft(state);
        s.held = null;
        s.tickets = s.tickets.filter((t) => t.id !== ticket.id);
        serve(s, ticket);
        return s;
      }
      if (held?.kind === "cup") {
        const ticket = drinkOnlyTicket(state, held.cup);
        if (ticket) {
          const s = draft(state);
          s.held = null;
          s.tickets = s.tickets.filter((t) => t.id !== ticket.id);
          serve(s, ticket);
          return s;
        }
        if (glassStatus(held.cup) !== "full" || state.ledge.length >= LEDGE_SIZE) return state;
        return { ...state, held: null, ledge: [...state.ledge, held.cup] };
      }
      if (held?.kind !== "basket") return state;
      const ticket = matchingTicket(state, held.basket.contents);
      if (!ticket) return state;
      const s = draft(state);
      s.held = null;
      s.tickets = s.tickets.filter((t) => t.id !== ticket.id);
      const drink = ticket.build?.drink;
      if (drink) s.ledge.splice(s.ledge.findIndex((c) => drinkMismatch(c, drink) === null), 1);
      serve(s, ticket);
      return s;
    }
    case "takeDrink": {
      if (held || !state.ledge.length) return state;
      return { ...state, held: { kind: "cup", cup: state.ledge.at(-1)! }, ledge: state.ledge.slice(0, -1) };
    }
    case "takeCup": {
      if (held || !state.shelf.tumbler) return state;
      const s = draft(state);
      s.shelf.tumbler = state.shelf.tumbler - 1;
      s.held = { kind: "cup", cup: { dish: { id: s.nextId++, kind: "tumbler", grime: 0, maxGrime: 0 }, drink: null, fill: 0 } };
      return s;
    }
    case "pour": {
      const k = state.setup.kitchen;
      if (held?.kind !== "cup" || !k?.pourSeconds) return state;
      const cup = held.cup;
      // One drink to a cup: tip out the wrong one first.
      if (cup.drink && cup.drink !== action.drink && cup.fill > 0) return state;
      const seconds = action.seconds ?? POUR_TAP;
      const fill = Math.min(MAX_FILL, cup.fill + seconds / k.pourSeconds);
      return { ...state, held: { kind: "cup", cup: { ...cup, drink: action.drink, fill } } };
    }
    case "trash": {
      if (held?.kind === "portafilter" || held?.kind === "coffee") return { ...state, held: null };
      if (held?.kind === "shot" || held?.kind === "pitcher") {
        const s = draft(state);
        s.held = { kind: "dish", dish: dirtyDish(s, held.kind === "shot" ? "shotglass" : "pitcher") };
        return s;
      }
      // A cup that's had soda in it is sticky: tipped out, it goes to the wash, not back on the stack.
      if (held?.kind === "cup") {
        if (!held.cup.drink) return state;
        const s = draft(state);
        s.held = { kind: "dish", dish: { ...dirtyDish(s, "tumbler"), id: held.cup.dish.id } };
        return s;
      }
      if (held?.kind === "patty" || held?.kind === "fries") return { ...state, held: null };
      if (held?.kind === "basket" && held.basket.contents !== EMPTY) return { ...state, held: { kind: "basket", basket: { ...held.basket, contents: EMPTY } } };
      return state;
    }
    case "grind": {
      const cafe = state.setup.cafe;
      if (!cafe || (held && held.kind !== "portafilter")) return state;
      const dose = held?.kind === "portafilter" ? held.dose : 0;
      const seconds = action.seconds ?? POUR_TAP;
      return { ...state, held: { kind: "portafilter", dose: Math.min(MAX_DOSE, dose + seconds / cafe.grindSeconds) } };
    }
    case "group": {
      const spot = state.groups[action.slot];
      if (spot === undefined) return state;
      if (held?.kind === "portafilter" && !spot) {
        if (doseStatus(held.dose) !== "right" || !state.shelf.shotglass) return state;
        const s = draft(state);
        s.shelf.shotglass = state.shelf.shotglass - 1;
        s.groups[action.slot] = { cook: 0 };
        s.held = null;
        return s;
      }
      if (held || !spot) return state;
      const s = draft(state);
      s.held = { kind: "shot", shot: spot };
      s.groups[action.slot] = null;
      return s;
    }
    case "takeMilk": {
      if (held || !state.shelf.pitcher) return state;
      const s = draft(state);
      s.shelf.pitcher = state.shelf.pitcher - 1;
      s.held = { kind: "pitcher", milk: { cook: 0 } };
      return s;
    }
    case "wand": {
      if (held?.kind === "pitcher" && !state.wand) return { ...state, wand: held.milk, held: null };
      if (!held && state.wand) return { ...state, held: { kind: "pitcher", milk: state.wand }, wand: null };
      return state;
    }
    case "takeCoffeeCup": {
      if (held || !state.setup.cafe) return state;
      return { ...state, held: { kind: "coffee", cup: EMPTY_CUP } };
    }
    case "bar": {
      const bar = state.bar;
      if (!held) return bar ? { ...state, held: { kind: "coffee", cup: bar }, bar: null } : state;
      if (held.kind === "coffee") return { ...state, bar: held.cup, held: bar ? { kind: "coffee", cup: bar } : null };
      if (held.kind !== "shot" && held.kind !== "pitcher") return state;
      // Into the cup on the bar, or a fresh one off the stack.
      const cup = bar ?? EMPTY_CUP;
      if (held.kind === "shot" && cup.shots.length >= MAX_SHOTS) return state;
      if (held.kind === "pitcher" && cup.milk) return state;
      const s = draft(state);
      s.bar = held.kind === "shot" ? { ...cup, shots: [...cup.shots, held.shot] } : { ...cup, milk: held.milk };
      s.held = { kind: "dish", dish: dirtyDish(s, held.kind === "shot" ? "shotglass" : "pitcher") };
      return s;
    }
    case "pump": {
      const bar = state.bar;
      if (!bar) return state;
      const syrups = bar.syrups.includes(action.syrup) ? bar.syrups.filter((x) => x !== action.syrup) : [...bar.syrups, action.syrup];
      return { ...state, bar: { ...bar, syrups } };
    }
  }
}

/** The oldest coffee ticket a cup is exactly right for. */
export function matchingCoffee(s: ShiftState, cup: CoffeeCup): Ticket | undefined {
  return s.tickets.find((t) => t.coffee && coffeeMismatch(cup, t.coffee) === null);
}

/** Why a cup can't go out: what's wrong with it for the ticket it's closest to, or null when it matches one. */
export function whyNotCoffee(s: ShiftState, cup: CoffeeCup): string | null {
  if (!s.tickets.length) return "No one's waiting on an order";
  if (matchingCoffee(s, cup)) return null;
  const best = s.tickets
    .filter((t) => t.coffee)
    .map((t) => ({ t, wrong: coffeeDifferences(cup, t.coffee!) }))
    .sort((a, b) => a.wrong - b.wrong)[0];
  return best ? `${best.t.name}'s ${best.t.item.toLowerCase()} ${coffeeMismatch(cup, best.t.coffee!)}` : "No one ordered this";
}

/** Patties or fries that are done: what an empty basket can be fetched for. */
function cookedFood(held: Held): boolean {
  return (held.kind === "patty" && pattyDoneness(held.patty) === "done") || (held.kind === "fries" && friesDoneness(held.fries) === "done");
}

/** A basket's contents with a patty or fries added, or null when it has no room for it. */
function addFood(c: Contents, food: Held): Contents | null {
  if (food.kind === "patty") return c.patties.length < MAX_PATTIES ? { ...c, patties: [...c.patties, food.patty] } : null;
  if (food.kind === "fries") return c.fries ? null : { ...c, fries: food.fries };
  return null;
}

function isEmpty(c: Contents): boolean {
  return !c.patties.length && !c.toppings.length && !c.fries;
}

/** A clean basket taken off the shelf (the caller checks there is one). */
function cleanBasket(s: ShiftState): Basket {
  s.shelf.basket = (s.shelf.basket ?? 0) - 1;
  return { dish: { id: s.nextId++, kind: "basket", grime: 0, maxGrime: 0 }, contents: EMPTY };
}

/** The oldest waiting ticket a basket's contents are exactly right for. */
export function matchingTicket(s: ShiftState, contents: Contents): Ticket | undefined {
  return s.tickets.find((t) => t.build && needsBasket(t.build) && mismatch(contents, t.build) === null && drinkReady(s, t.build.drink));
}

/** Whether a ticket's drink (if it wants one) is poured and waiting on the ledge. */
export function drinkReady(s: ShiftState, drink: Drink | undefined): boolean {
  return !drink || s.ledge.some((c) => drinkMismatch(c, drink) === null);
}

/** The oldest drinks-only ticket a cup is right for. */
export function drinkOnlyTicket(s: ShiftState, cup: Glass): Ticket | undefined {
  return s.tickets.find((t) => t.build?.drink && !needsBasket(t.build) && drinkMismatch(cup, t.build.drink) === null);
}

/**
 * What a cup in your hands is for at the window, or what's wrong with it:
 * out to a drinks-only ticket, onto the ledge for a ticket's food, or why not.
 */
export function cupPlan(s: ShiftState, cup: Glass): { ticket?: Ticket; ledge?: true; why?: string } {
  const only = drinkOnlyTicket(s, cup);
  if (only) return { ticket: only };
  const status = glassStatus(cup);
  if (status === "empty") return { why: "Fill the cup at the soda machine" };
  if (status === "short") return { why: `The ${DRINK_NAMES[cup.drink!]}'s not full yet` };
  if (status === "spilled") return { why: `It's spilling over: tip it out` };
  if (s.ledge.length >= LEDGE_SIZE) return { why: "The ledge is full" };
  return { ledge: true };
}

/**
 * Why a basket can't go out: what's wrong with it for the ticket it's
 * closest to (the oldest one with the fewest things wrong), or null when it
 * matches one.
 */
export function whyNot(s: ShiftState, contents: Contents): string | null {
  if (!s.tickets.length) return "No one's waiting on an order";
  if (matchingTicket(s, contents)) return null;
  const scored = s.tickets
    .filter((t) => t.build && needsBasket(t.build))
    .map((t) => ({ t, wrong: differences(contents, t.build!) }))
    .sort((a, b) => a.wrong - b.wrong);
  const best = scored[0];
  if (!best) return "No one ordered this";
  const wrong = mismatch(contents, best.t.build!);
  if (wrong) return `${best.t.item} ${wrong}`;
  return `${best.t.item} needs its ${DRINK_NAMES[best.t.build!.drink!]} on the ledge`;
}

/** How many things differ between a basket and a build, for picking the closest ticket. */
function differences(c: Contents, b: Build): number {
  let n = Math.abs(c.patties.length - b.patties) + (b.fries === !!c.fries ? 0 : 1);
  for (const t of b.toppings) if (!c.toppings.includes(t)) n++;
  for (const t of c.toppings) if (!b.toppings.includes(t)) n++;
  return n;
}

/**
 * What's in the sink: nothing, a dish to scrub, a clean dish waiting for rack
 * space, or a clean hand-washed dish (a wok) waiting to be lifted out.
 */
export function sinkStatus(s: ShiftState): "empty" | "dirty" | "blocked" | "rinsed" {
  if (!s.sink) return "empty";
  if (s.sink.grime > 0) return "dirty";
  return DISHES[s.sink.kind].handWash ? "rinsed" : "blocked";
}

export function machineStatus(s: ShiftState): "idle" | "washing" | "done" {
  if (!s.machine) return "idle";
  return s.t < s.machine.doneAt ? "washing" : "done";
}

export interface ShiftResult {
  orders: number;
  served: number;
  walkouts: number;
  breaks: number;
  tips: number;
  wage: number;
  breakage: number;
  pay: number;
  /** One per goal, in order. */
  goalsMet: boolean[];
  /** The shift second the last dish was put away. */
  closedAt: number;
  stars: number;
}

export function goalMet(goal: StarGoal, stats: ShiftStats): boolean {
  const servedShare = stats.orders ? stats.served / stats.orders : 1;
  if (goal.minServed !== undefined && servedShare < goal.minServed) return false;
  if (goal.maxWalkouts !== undefined && stats.walkouts > goal.maxWalkouts) return false;
  if (goal.maxBreaks !== undefined && stats.breaks > goal.maxBreaks) return false;
  return true;
}

export function shiftResult(s: ShiftState): ShiftResult {
  const { setup, stats } = s;
  const goalsMet = setup.goals.map((g) => goalMet(g, stats));
  const firstMissed = goalsMet.indexOf(false);
  const breakage = stats.breaks * setup.breakCost;
  return {
    ...stats,
    tips: round2(stats.tips),
    wage: setup.wage,
    breakage,
    pay: Math.max(0, round2(setup.wage + stats.tips - breakage)),
    goalsMet,
    stars: firstMissed < 0 ? goalsMet.length : firstMissed,
    closedAt: s.t,
  };
}

/** The board spot toppings go on: the one you last worked at, or else the only basket down; -1 with none. */
export function boardSlot(s: ShiftState): number {
  return s.board[s.boardFocus] ? s.boardFocus : s.board.findIndex(Boolean);
}

/** The basket on the board that toppings go on, if there's one down. */
export function boardBasket(s: ShiftState): Basket | null {
  const slot = boardSlot(s);
  return slot < 0 ? null : s.board[slot]!;
}

/** Whether it's past closing time: no more orders, only closing up. */
export function closing(s: ShiftState): boolean {
  return s.t >= s.setup.shiftSeconds;
}

/**
 * Dishes not yet back on the shelf, clean: in the tub, the sink, the rack or
 * the machine, in your hands, on the board or the ledge, or still out on the
 * tables. Closing up is getting this to nothing.
 */
export function dishesOut(s: ShiftState): number {
  const held = s.held;
  // Food and paper cups aren't dishes; a shot is in its glass, milk in its pitcher.
  const inHand = !held ? 0 : held.kind === "rack" ? held.dishes.length : held.kind === "patty" || held.kind === "fries" || held.kind === "portafilter" || held.kind === "coffee" ? 0 : 1;
  return (
    s.tub.length +
    (s.sink ? 1 : 0) +
    s.rack.length +
    (s.machine?.dishes.length ?? 0) +
    inHand +
    s.board.filter(Boolean).length +
    s.ledge.length +
    s.groups.filter(Boolean).length +
    (s.wand ? 1 : 0) +
    s.diners.reduce((n, d) => n + d.dishes.length, 0)
  );
}

/** The wall-clock time at shift second `t`, e.g. "8:45 AM". */
export function clockTime(setup: ShiftSetup, t: number): string {
  const [open, close] = setup.hours;
  const minutes = Math.floor((open + ((close - open) * t) / setup.shiftSeconds) * 60);
  const h24 = Math.floor(minutes / 60) % 24;
  const mm = String(minutes % 60).padStart(2, "0");
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm} ${h24 < 12 ? "AM" : "PM"}`;
}

// --- internals -------------------------------------------------------------

/** A copy whose containers can be changed freely without touching `state`. */
function draft(state: ShiftState): ShiftState {
  return {
    ...state,
    tickets: [...state.tickets],
    diners: [...state.diners],
    shelf: { ...state.shelf },
    tub: [...state.tub],
    held: state.held && (state.held.kind === "rack" ? { ...state.held, dishes: [...state.held.dishes] } : { ...state.held }),
    sink: state.sink && { ...state.sink },
    rack: [...state.rack],
    machine: state.machine && { ...state.machine, dishes: [...state.machine.dishes] },
    griddle: [...state.griddle],
    fryer: [...state.fryer],
    board: [...state.board],
    stats: { ...state.stats },
    events: [...state.events],
    customers: [...state.customers],
    groups: [...state.groups],
    ledge: [...state.ledge],
  };
}

/**
 * Customers out front: the next one sets off up the boardwalk in time to
 * reach the kiosk as their order's due, and the ones who've walked off are
 * forgotten. Their order still lands at `nextOrderAt`, as it would without them.
 */
function arrive(s: ShiftState): void {
  const no = s.stats.orders + 1;
  const due = s.nextOrderAt;
  if (due <= s.setup.lastOrderAt && !s.customers.some((c) => c.no === no) && s.t >= due - leadSeconds(no)) {
    s.customers.push({ no, comesAt: Math.max(0, due - leadSeconds(no)), orderAt: due });
  }
  if (s.customers.some((c) => customerGone(c, s.t))) s.customers = s.customers.filter((c) => !customerGone(c, s.t));
}

/** A customer gets their order, or gives up. */
function finish(s: ShiftState, no: number, outcome: "served" | "walkout"): void {
  s.customers = s.customers.map((c) => (c.no === no && c.doneAt === undefined ? { ...c, doneAt: s.t, outcome } : c));
}

function random(s: ShiftState): number {
  const [value, next] = nextRandom(s.rng);
  s.rng = next;
  return value;
}

function randomInt(s: ShiftState, [min, max]: [number, number]): number {
  return min + Math.floor(random(s) * (max - min + 1));
}

function dirtyDish(s: ShiftState, kind: DishKind): Dish {
  const grime = randomInt(s, DISHES[kind].grime);
  return { id: s.nextId++, kind, grime, maxGrime: grime };
}

function orderGap(s: ShiftState, at: number): number {
  const { rush, shiftSeconds } = s.setup;
  const share = at / shiftSeconds;
  const every = (rush.find((seg) => share < seg.until) ?? rush[rush.length - 1]!).every;
  return every * (0.7 + 0.6 * random(s));
}

function placeOrder(s: ShiftState, at: number): void {
  const { menu } = s.setup;
  const item = menu[Math.floor(random(s) * menu.length)]!;
  const ticket: Ticket = { id: s.nextId++, no: s.stats.orders + 1, item: item.name, dishes: item.dishes, placedAt: at };
  if (item.build) ticket.build = item.build;
  if (item.coffee) {
    ticket.coffee = item.coffee;
    ticket.name = nameFor(ticket.no);
  }
  s.tickets.push(ticket);
  s.stats.orders++;
}

/** The espresso machine and the steam wand: shots pull and milk heats, and both go over if they're left. */
function brew(s: ShiftState, c: CafeSetup, dt: number): void {
  s.groups = s.groups.map((shot) => {
    if (!shot) return shot;
    const next = { cook: shot.cook + dt / c.shotSeconds };
    if (shot.cook < FRIES_BURNT && next.cook >= FRIES_BURNT) log(s, "burnt", "A shot ran too long: it's bitter!");
    return next;
  });
  if (s.wand) {
    const next = { cook: s.wand.cook + dt / c.steamSeconds };
    if (s.wand.cook < FRIES_BURNT && next.cook >= FRIES_BURNT) log(s, "burnt", "The milk scalded!");
    s.wand = next;
  }
}

/** The heat: the down side of every patty on the flat-top, and every portion in the fryer. Burnt food stays put. */
function cook(s: ShiftState, k: KitchenSetup, dt: number): void {
  s.griddle = s.griddle.map((p) => {
    if (!p) return p;
    const before = pattyDoneness(p);
    const next: Patty = { sides: [p.sides[0] + dt / k.pattySideSeconds, p.sides[1]] };
    if (before !== "burnt" && pattyDoneness(next) === "burnt") log(s, "burnt", "A patty burnt on the flat-top!");
    return next;
  });
  s.fryer = s.fryer.map((f) => {
    if (!f) return f;
    const next = { cook: f.cook + dt / k.friesSeconds };
    if (friesDoneness(f) !== "burnt" && friesDoneness(next) === "burnt") log(s, "burnt", "The fries burnt!");
    return next;
  });
}

/** A ticket goes out: the tip (smaller the longer it waited), and the dishes come back dirty later. */
function serve(s: ShiftState, ticket: Ticket): void {
  const wait = Math.max(0, s.t - ticket.placedAt);
  s.stats.served++;
  finish(s, ticket.no, "served");
  const soda = ticket.build?.drink && ticket.build.drink !== "water" ? SODA_TIP : 0;
  const tip = (s.setup.tip + soda) * (1 - 0.5 * Math.min(1, wait / s.setup.patience));
  s.stats.tips += tip;
  log(s, "served", ticket.item, round2(tip));
  const table = ticket.dishes.filter((k) => !DISHES[k].backFromLine);
  if (table.length) s.diners.push({ dishes: table, doneAt: s.t + randomInt(s, s.setup.eatSeconds) });
  // Cookware comes back from the line once the order's plated.
  for (const k of ticket.dishes) {
    const back = DISHES[k].backFromLine;
    if (back) s.diners.push({ dishes: [k], doneAt: s.t + back });
  }
}

/** At a shelf restaurant: send out every waiting ticket the shelf can cover, oldest first. */
function serveFromShelf(s: ShiftState): void {
  const waiting: Ticket[] = [];
  for (const ticket of s.tickets) {
    if (!canCover(s.shelf, ticket.dishes)) {
      waiting.push(ticket);
      continue;
    }
    for (const kind of ticket.dishes) s.shelf[kind] = (s.shelf[kind] ?? 0) - 1;
    serve(s, ticket);
  }
  s.tickets = waiting;
}

function canCover(shelf: Partial<Record<DishKind, number>>, dishes: DishKind[]): boolean {
  const need: Partial<Record<DishKind, number>> = {};
  for (const kind of dishes) need[kind] = (need[kind] ?? 0) + 1;
  return Object.entries(need).every(([kind, n]) => (shelf[kind as DishKind] ?? 0) >= n);
}

/** The dishes a ticket is short of, as words: "plates and mugs". */
function missingFor(s: ShiftState, ticket: Ticket): string {
  const short = [...new Set(ticket.dishes)].filter(
    (k) => (s.shelf[k] ?? 0) < ticket.dishes.filter((d) => d === k).length,
  );
  const words = (short.length ? short : ticket.dishes).map((k) => DISHES[k].plural);
  return words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}` : words[0]!;
}

/** A dirty dish comes back from a table: into the tub, or onto the floor if the tub's full. */
function bus(s: ShiftState, kind: DishKind): void {
  if (s.tub.length >= s.setup.tubCapacity) {
    s.stats.breaks++;
    log(s, "break", `Crash! The tub overflowed and a ${DISHES[kind].name.toLowerCase()} broke.`);
    return;
  }
  s.tub.push(dirtyDish(s, kind));
}

/** A clean dish in the sink drops into the rack once there's a free slot. */
function settleSink(s: ShiftState): void {
  if (s.sink && s.sink.grime === 0 && !DISHES[s.sink.kind].handWash && s.rack.length < s.setup.rackSlots) {
    s.rack.push(s.sink);
    s.sink = null;
  }
}

function log(s: ShiftState, kind: ShiftEventKind, text: string, tip?: number): void {
  s.events.push(tip === undefined ? { id: s.nextId++, at: s.t, kind, text } : { id: s.nextId++, at: s.t, kind, text, tip });
  if (s.events.length > EVENTS_KEPT) s.events.splice(0, s.events.length - EVENTS_KEPT);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
